import { Place, type Property } from '@kalos/kalos-rpc';
import { useQuery } from '@tanstack/react-query';

import { usePropertiesGetQuery } from '../../../../hooks/react-query/usePropertiesQuery';
import { useActiveServiceAreaQuery } from '../../../../hooks/react-query/useServiceAreaQuery';
import { MapClientService } from '../../../../tools/helpers';
import { milesOutsideServiceArea, type ServiceAreaPoint } from '../../../../tools/serviceArea';

type Address = Pick<Property, 'address' | 'city' | 'state' | 'zip'>;

export type ServiceAreaCheck = {
  /** Miles outside the boundary; 0 when inside or when the location is unknown. */
  milesOutside: number;
  /** The address could not be placed on the map, so the boundary was not checked. */
  unlocatable: boolean;
};

const NOT_CHECKED: ServiceAreaCheck = { milesOutside: 0, unlocatable: false };

// Built from the property's own fields rather than through addressStringToPlace,
// which re-parses a string assembled from those same fields and throws outright
// on an address with no city or zip.
const placeOf = ({ address, city, state, zip }: Address): Place => {
  const [first, ...rest] = address.trim().split(' ');
  const streetNumber = Number(first);
  const hasStreetNumber = first !== '' && !Number.isNaN(streetNumber);

  return Place.create({
    streetNumber: hasStreetNumber ? streetNumber : 0,
    roadName: hasStreetNumber ? rest.join(' ') : address.trim(),
    city,
    state,
    zipCode: zip,
  });
};

/**
 * Where the property sits relative to the Kalos service area: how many miles
 * outside it, or that its address could not be placed at all. Shared by the
 * Review step (Step 2) and the Schedule step (Step 3) so the banner says the
 * same thing in both.
 *
 * Residential jobs only. `isResidential` is the job's Sector; while it is unset
 * (Step 2, or -1 on Step 3) the property's own sector stands in.
 *
 * The check runs in the browser rather than asking the backend, because the
 * operator has to see the warning before anything is saved. The backend stamps
 * event.is_outside_service_area on save from the property's *stored*
 * coordinates, so a brand-new address that is only geocoded here will warn and
 * still save with the flag at 0. That disagreement is accepted: the backend will
 * not guess at coordinates it does not have, and the banner is advisory.
 */
export const useOutsideServiceArea = (
  property: Property,
  isResidential?: number,
): ServiceAreaCheck => {
  const { data: serviceArea } = useActiveServiceAreaQuery();

  // The flow keeps the property it was handed and never refetches it. Re-reading
  // the row here means an address edited in another tab (the card's Property link)
  // is seen on refocus, along with the coordinates the backend re-geocoded for it
  // -- or cleared, when the new address would not geocode.
  const { data: fresh } = usePropertiesGetQuery({
    filter: { id: property.id },
    enabled: property.id > 0,
  });
  const current = fresh ?? property;
  const { address, city, state, zip } = current;

  const sector = isResidential === 0 || isResidential === 1 ? isResidential : current.isResidential;
  const isResidentialJob = sector === 1;

  const stored: ServiceAreaPoint | null =
    current.geolocationLat && current.geolocationLng
      ? { lat: current.geolocationLat, lng: current.geolocationLng }
      : null;

  // Only for an address the property has no coordinates for -- a newly entered
  // one, or one the backend could not place. Keyed on the address parts rather
  // than the property id so re-selecting the same property does not geocode twice.
  const canGeocode = !!address && (!!zip || !!city);
  const geocodeQuery = useQuery({
    queryKey: ['serviceAreaGeocode', address, city, state, zip],
    queryFn: async (): Promise<ServiceAreaPoint | null> => {
      const res = await MapClientService.Geocode(placeOf({ address, city, state, zip }));
      if (!res?.latitude || !res?.longitude) return null;
      return { lat: res.latitude, lng: res.longitude };
    },
    enabled: isResidentialJob && !stored && canGeocode,
    staleTime: Infinity,
  });

  if (!isResidentialJob || !serviceArea || serviceArea.polygon.length < 3) return NOT_CHECKED;

  const point = stored ?? geocodeQuery.data ?? null;
  if (point) {
    return {
      milesOutside: milesOutsideServiceArea(point, serviceArea.polygon),
      unlocatable: false,
    };
  }

  // Wait for the geocode to settle so the note does not flash while it runs.
  const settled = !canGeocode || geocodeQuery.isSuccess || geocodeQuery.isError;
  return { milesOutside: 0, unlocatable: settled };
};
